import { removeTrapFocus, trapFocus } from "@theme/focus";

class LoyaltyPage extends HTMLElement {
    /** @type {MutationObserver|null} */
    #observer = null;

    /** @type {WeakSet<HTMLElement>} */
    #boundTriggers = new WeakSet();

    /** @type {MutationObserver|null} */
    #transactionHistoryObserver = null;

    /** @type {HTMLElement|null} */
    #transactionHistoryTableBody = null;

    /** @type {number} */
    totalPoints = 0;

    /** @type {number} */
    nextTierPoints = 0;

    /** @type {number} */
    totalPointsNeed = 0;

    connectedCallback() {
        this.#observeLoyaltyPage();
    }

    disconnectedCallback() {
        this.#observer?.disconnect();
        this.#transactionHistoryObserver?.disconnect();
    }

    init() {
            this.#applyTemplateReplacements();

        const loyaltyProgressSection = this.querySelector("#loyalty-progress");
        if (!(loyaltyProgressSection instanceof HTMLElement)) return;

        const remaining = this.#calculateRemainingPoints(loyaltyProgressSection);
        loyaltyProgressSection.innerHTML = loyaltyProgressSection.innerHTML.replace(
            /\[nextTierPointsRemining\]/g,
            String(remaining)
        );

        this.#resolveCurrentTierPoints(loyaltyProgressSection);
        this.#setProgressBarWidth(loyaltyProgressSection);
    }

    /**
     * @param {HTMLElement} section
     */
    #resolveCurrentTierPoints(section) {
        const loyaltyPage = this.querySelector("#appstleLoyaltyPage");
        if (!(loyaltyPage instanceof HTMLElement)) return;

        const vipSection = loyaltyPage.querySelector("#appstleLoyaltyPageV2VipSection");
        if (!(vipSection instanceof HTMLElement)) return;

        const currentTierLabel = vipSection.querySelector(".appstleLoyaltyPageV2VipCurrentTierLabel");
        if (!(currentTierLabel instanceof HTMLElement)) return;

        const listItem = currentTierLabel.closest(".appstleLoyaltyPageV2VipListItem");
        if (!(listItem instanceof HTMLElement)) return;

        const description = listItem.querySelector(".appstleLoyaltyPageV2VipListItemDescription");
        if (!(description instanceof HTMLElement)) return;

        const digits = description.textContent?.match(/[\d,.]+/)?.[0]?.replace(/,/g, "") ?? "";
        section.innerHTML = section.innerHTML.replace(/\[currentTierPoints\]/g, digits);
    }

    /**
     * @param {HTMLElement} section
     */
    #setProgressBarWidth(section) {
        if (this.totalPointsNeed <= 0) return;

        const progress = Math.min((this.totalPoints / this.totalPointsNeed) * 100, 100);
        section.style.setProperty("--progress-bar-width", `${progress}%`);
    }

    /**
     * @param {HTMLElement} section
     * @returns {number}
     */
    #calculateRemainingPoints(section) {
        const totalPointsEl = section.querySelector("#total-points");
        const nextTierPointsEl = section.querySelector("#next-tier-points");

        this.totalPoints = parseFloat(totalPointsEl?.textContent ?? "") || 0;
        this.nextTierPoints = parseFloat(nextTierPointsEl?.textContent ?? "") || 0;
        this.totalPointsNeed = this.nextTierPoints + this.totalPoints;

        return this.totalPointsNeed;
    }

    /**
     * @param {string} text
     * @returns {string|null}
     */
    #parseHistoryDateToShortFormat(text) {
        const trimmed = text.trim();
        if (!trimmed) return null;
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) return null;

        const parsed = new Date(trimmed);
        if (Number.isNaN(parsed.getTime())) return null;

        return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
    }

    /**
     * @param {HTMLElement} tableBody
     */
    #formatHistoryTimeDates(tableBody) {
        const cells = tableBody.querySelectorAll(".history-time-date");

        for (const cell of cells) {
            if (!(cell instanceof HTMLElement)) continue;

            const formatted = this.#parseHistoryDateToShortFormat(cell.textContent ?? "");
            if (formatted === null) continue;

            cell.textContent = formatted;
        }
    }

    /** @type {HTMLElement|null} */
    #vipContainer = null;

    /** @type {boolean} */
    #vipModalBound = false;

    /** @type {HTMLElement|null} */
    #lastVipTrigger = null;
    /**
     * Stores a reference to the VIP container and wires the dialog open/close
     * behaviour (only once). The <dialog> lives directly in the custom element
     * so it never participates in page flow while closed.
     * @param {HTMLElement} vipContainer
     */
    #setupVipModal(vipContainer) {
        this.#vipContainer = vipContainer;

        if (this.#vipModalBound) return;
        this.#vipModalBound = true;

        const dialog = this.querySelector("#loyalty-vip-modal");
        if (!(dialog instanceof HTMLDialogElement)) return;


        const body = dialog.querySelector("#loyalty-vip-modal-body");
        if (this.#vipContainer && body instanceof HTMLElement) {
            // Clear previous content and clone the inner children fresh each open
            body.replaceChildren(
                ...Array.from(this.#vipContainer.children).map((child) => child.cloneNode(true))
            );
        }

        const closeBtn = dialog.querySelector("[data-loyalty-vip-modal-close]");
        closeBtn?.addEventListener("click", () => dialog.close());

        // Backdrop clicks bubble up with e.target === dialog itself
        dialog.addEventListener("click", (e) => {
            if (e.target === dialog) dialog.close();
        });

        // Follow project scroll-lock pattern used in base.css (html[scroll-lock])
        dialog.addEventListener("close", () => {
            document.documentElement.removeAttribute("scroll-lock");
            removeTrapFocus();
            if (this.#lastVipTrigger instanceof HTMLElement) {
                this.#lastVipTrigger.focus();
            }
        });
    }

    /**
     * Finds the .loyalty-vip-modal__trigger button injected by Appstle and
     * wires it to open the modal (only once per button instance).
     */
    #ensureVipModalTrigger() {
        const trigger = this.querySelector(".loyalty-vip-modal__trigger");
        if (!(trigger instanceof HTMLElement)) return;

        if (this.#boundTriggers.has(trigger)) return;
        this.#boundTriggers.add(trigger);

        trigger.addEventListener("click", () => {
            const dialog = this.querySelector("#loyalty-vip-modal");
            if (!(dialog instanceof HTMLDialogElement)) return;

            this.#lastVipTrigger = trigger;
            document.documentElement.setAttribute("scroll-lock", "");
            dialog.showModal();
            trapFocus(dialog);

            const closeBtn = dialog.querySelector("[data-loyalty-vip-modal-close]");
            if (closeBtn instanceof HTMLElement) closeBtn.focus();
        });
    }

    #applyTemplateReplacements() {
        const template = this.querySelector("template");
        if (!(template instanceof HTMLTemplateElement)) return;

        const replacementNodes = template.content.querySelectorAll("[data-selector-for-replace]");
        if (!replacementNodes.length) return;

        for (const node of replacementNodes) {
            if (!(node instanceof HTMLElement)) continue;

            const selector = node.dataset.selectorForReplace?.trim();
            if (!selector) continue;

            const target = this.querySelector(selector);
            if (!(target instanceof HTMLElement)) continue;

            const replacement = node.cloneNode(true);
            if (!(replacement instanceof HTMLElement)) continue;

            replacement.removeAttribute("data-selector-for-replace");
            target.replaceWith(replacement);
        }
    }

    /**
     * @param {HTMLElement} loyaltyPage
     */
    #ensureTransactionHistoryObserver(loyaltyPage) {
        const body = loyaltyPage.querySelector(".transaction-history-table-body");
        if (!(body instanceof HTMLElement)) return;

        if (this.#transactionHistoryTableBody !== body) {
            this.#transactionHistoryObserver?.disconnect();
            this.#transactionHistoryTableBody = body;
            this.#formatHistoryTimeDates(body);

            this.#transactionHistoryObserver = new MutationObserver(() => {
                this.#formatHistoryTimeDates(body);
            });

            this.#transactionHistoryObserver.observe(body, { childList: true, subtree: true });
        }
    }

    #observeLoyaltyPage() {
        const loyaltyPage = this.querySelector("#appstleLoyaltyPage");
        if (!(loyaltyPage instanceof HTMLElement)) return;

            this.#applyTemplateReplacements();
        this.#ensureTransactionHistoryObserver(loyaltyPage);

        this.#observer = new MutationObserver((mutations) => {
            this.#applyTemplateReplacements();
            this.#ensureTransactionHistoryObserver(loyaltyPage);
            // Always attempt to bind the trigger — it may appear in any mutation
            this.#ensureVipModalTrigger();

            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (!(node instanceof HTMLElement)) continue;

                    if (node.id === "appstleLoyaltyPageV2BrandingSection") {
                        node.remove();
                        continue;
                    }

                    const branding = node.querySelector("#appstleLoyaltyPageV2BrandingSection");
                    if (branding instanceof HTMLElement) {
                        branding.remove();
                    }

                    const vipContainer = node.classList.contains("appstleLoyaltyPageV2VipSectionContainer")
                        ? node
                        : node.querySelector(".appstleLoyaltyPageV2VipSectionContainer");

                    if (vipContainer instanceof HTMLElement) {
                        this.#setupVipModal(vipContainer);
                    }

                    if (node.id === "loyalty-progress" || node.querySelector("#loyalty-progress")) {
                        this.init();
                        return;
                    }
                }
            }
        });

        this.#observer.observe(loyaltyPage, { childList: true, subtree: true });
    }
}

if (!customElements.get("loyalty-page")) {
    customElements.define("loyalty-page", LoyaltyPage);
}